// frontend/src/pages/Complemento.js
import React, { useEffect, useState } from "react";
import "./Complemento.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import { FaEdit } from "react-icons/fa";

import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function Complemento() {
  const [complementos, setComplementos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoComplemento, setNovoComplemento] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [complementosPorPagina, setComplementosPorPagina] = useState(5);
  const [loading, setLoading] = useState(true);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("complementoGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("complementoGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);

    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch("/api/complementos", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.complementos) ? data.complementos : [];
        setComplementos(lista);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar Complementos:", err);
        setLoading(false);
      });
  }, []);

  const handleCadastrar = (e) => {
    e.preventDefault();

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar complementos.");
      return;
    }

    const token = localStorage.getItem("token");
    const nomeFormatado = novoComplemento.trim().toLowerCase();

    const jaExiste = complementos.some((c) => c.nome.trim().toLowerCase() === nomeFormatado);
    if (jaExiste) {
      alert("Esse Complemento já está cadastrado");
      return;
    }

    if (!novoComplemento.trim()) {
      alert("Preencha o campo Complemento.");
      return;
    }

    fetch("/api/complementos", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        nome: novoComplemento.toUpperCase(),
      }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para cadastrar.");
          return;
        }
        const data = await res.json();

        const novoItem = {
          id: data.id,
          nome: (data.nome ?? novoComplemento).toUpperCase(),
        };

        setComplementos((prev) => [novoItem, ...prev]);

        setPaginaAtual(1);
        setNovoComplemento("");
      })
      .catch((err) => console.error("Erro ao cadastrar complemento:", err));
  };

  const handleSaveEdit = (id) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar complementos.");
      return;
    }

    const token = localStorage.getItem("token");
    fetch(`/api/complementos/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        nome: nomeEditado.toUpperCase(),
      }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para editar.");
          return;
        }

        setComplementos((prev) => prev.map((c) => (c.id === id ? { ...c, nome: nomeEditado } : c)));
        setEditandoId(null);
        setNomeEditado("");
      })
      .catch((err) => console.error("Erro ao editar complemento:", err));
  };

  const handleEdit = (complemento) => {
    setEditandoId(complemento.id);
    setNomeEditado(complemento.nome);
  };

  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir complementos.");
      return;
    }

    const token = localStorage.getItem("token");
    fetch(`/api/complementos/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para excluir.");
          return;
        }

        setComplementos((prev) => prev.filter((c) => c.id !== id));
      })
      .catch((err) => console.error("Erro ao excluir Complemento:", err));
  };

  const complementosFiltrados = complementos.filter((c) => c.nome.toLowerCase().includes(filtro.toLowerCase()));

  const indexInicial = (paginaAtual - 1) * complementosPorPagina;
  const indexFinal = indexInicial + complementosPorPagina;
  const complementosPaginados = complementosFiltrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(complementosFiltrados.length / complementosPorPagina);

  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Complemento"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const linhas = (complementosFiltrados || []).map((c) => [c.nome ?? ""].map(csvEscape));
    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "complementos.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="complemento-container">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

        <h2>Complemento</h2>

        <div className="bloco-linha">
          <TextField
            label="Buscar Complemento"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      fontSize="small"
                      sx={{
                        color: "var(--ig-t-9ca3af)",
                        display: "block",
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => setFiltro("")}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = complementosFiltrados.map((c) => c.nome).join(", ");

                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }

                const ok = await copiarTexto(texto);
                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando Complementos...</p>
            ) : complementosFiltrados.length === 0 ? (
              <p className="erro">Nenhum complemento cadastrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Complemento</th>

                      <th
                        style={{
                          width: "300px",
                          textAlign: "center",
                        }}
                      >
                        Ações
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {complementosPaginados.map((complemento) => (
                      <tr key={complemento.id}>
                        <td
                          className="nome-complemento"
                          style={{
                            paddingRight: "16px",
                          }}
                        >
                          {editandoId === complemento.id ? (
                            <input
                              value={nomeEditado}
                              onChange={(e) => setNomeEditado(e.target.value.toUpperCase())}
                              autoFocus
                              className="campo-formulario-padrao"
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                background: "transparent",
                                border: "2px solid #1fa3ff",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                color: "var(--ig-t-ffffff)",
                                fontWeight: 600,
                                outline: "none",
                              }}
                            />
                          ) : (
                            complemento.nome
                          )}
                        </td>

                        <td
                          className="botoes-acao"
                          style={{
                            width: "220px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            {editandoId === complemento.id ? (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<i className="fas fa-floppy-disk" />}
                                onClick={() => handleSaveEdit(complemento.id)}
                                sx={{
                                  minWidth: 118,
                                  height: 40,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#fff",
                                  backgroundColor: "#2e7d32",
                                  "&:hover": {
                                    backgroundColor: "#1b5e20",
                                  },
                                }}
                              >
                                Salvar
                              </Button>
                            ) : (
                              <>
                                {podeEditar && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<FaEdit />}
                                    onClick={() => handleEdit(complemento)}
                                    sx={{
                                      minWidth: 118,
                                      height: 40,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      color: "#fff",
                                      backgroundColor: "#1998a8",
                                      "&:hover": {
                                        backgroundColor: "#177f8d",
                                      },
                                    }}
                                  >
                                    Editar
                                  </Button>
                                )}

                                {podeExcluir && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<i className="fas fa-trash" />}
                                    onClick={() => handleDelete(complemento.id)}
                                    sx={{
                                      minWidth: 118,
                                      height: 40,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      color: "#fff",
                                      backgroundColor: "#d32f2f",
                                      "&:hover": {
                                        backgroundColor: "#b71c1c",
                                      },
                                    }}
                                  >
                                    Excluir
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={complementosFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={complementosPorPagina}
                  onRowsPerPageChange={(e) => {
                    setComplementosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}

                  labelRowsPerPage="Complementos por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}

                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {podeVisualizar && !podeCadastrar && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        {podeCadastrar && (
          <div className="bloco-formulario">
            <Box
              component="form"
              onSubmit={handleCadastrar}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                id="complemento"
                label="Complemento"
                value={novoComplemento}
                onChange={(e) => setNovoComplemento(e.target.value.toUpperCase())}
                placeholder="Digite o nome do Complemento"
                required={false}
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16.5px 14px",
                  },
                  "& .MuiInputLabel-root": {
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "transparent !important",
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: "16.5px 14px",
                    backgroundColor: "transparent !important",
                  },

                  "& .MuiInputBase-input": {
                    backgroundColor: "transparent !important",
                  },

                  "& fieldset": {
                    backgroundColor: "transparent !important",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={<i className="fas fa-floppy-disk" />}
                sx={{
                  width: 240,
                  minWidth: 240,
                  maxWidth: 240,
                  height: 52,
                  minHeight: 52,

                  alignSelf: "center",

                  padding: "0 20px",

                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: 1,

                  color: "#fff",
                  backgroundColor: "#2e7d32",

                  borderRadius: "6px",
                  boxShadow: "none",

                  "&:hover": {
                    backgroundColor: "#1b5e20",
                    boxShadow: "none",
                  },
                }}
              >
                Cadastrar
              </Button>
            </Box>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Complemento;
